import { Agent, Agent_LicenseFilter, Agent_QuickFilter, Agent_Status, Agent_WhereInput, AgentNamesDocument, AgentNamesQuery, AgentNamesQueryVariables } from '@/graphql/codegen/generated/graphql';
import { inject, ref, StyleValue } from 'vue';
import COLORS from '@/design-system/colors.json';
import { app } from '@/bootloader';
import { IKAgentsDrawerFilter } from '@/types/injectionKeys';
import { NoUndefinedField } from '@/types/utils';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import apolloClient from '@/plugins/GraphQL/Apollo/client';
import { IKAgentsSelected } from './agents-table.InjectionKeys';
import { IKAgentsQueryFilter } from '../../agents.injectionKeys';
import { AgentsFilterSelection } from './types/agents-table.types';

export const useAgentsSelectedProvider = () => {
  const coordinatorsService = inject(IKCoordinatorsService);
  const agentsDrawerFilter = inject(IKAgentsDrawerFilter);
  const agentsQueryFilter = inject(IKAgentsQueryFilter);

  const FILTER_SELECTION_MAP: Record<AgentsFilterSelection, Agent_WhereInput> = {
    [AgentsFilterSelection.All]: {
      quickFilter: Agent_QuickFilter.All,
    },
    [AgentsFilterSelection.Online]: {
      quickFilter: Agent_QuickFilter.Online,
    },
    [AgentsFilterSelection.IncompatibleVersion]: {
      quickFilter: Agent_QuickFilter.IncompatibleVersion,
    },
    [AgentsFilterSelection.Offline]: {
      statusFilter: [Agent_Status.Offline],
    },
    [AgentsFilterSelection.Cloud]: {
      licenseFilter: [Agent_LicenseFilter.CloudHelper],
    },
    [AgentsFilterSelection.DuplicateLicense]: {
      quickFilter: Agent_QuickFilter.DuplicateLicense,
    },
  };

  const loading = ref(false);
  const selectedAgentIds = ref<Set<NoUndefinedField<Agent>['id']>>(new Set());

  const clearSelection = () => {
    selectedAgentIds.value = new Set();
  };

  const removeAgentFromSelectedAgentIds = (agentId: string) => {
    selectedAgentIds.value.delete(agentId);
  };

  const getFilterForPayload = (filterType: AgentsFilterSelection) => {
    if (filterType === AgentsFilterSelection.Online) {
      return agentsDrawerFilter?.GQL_AgentsFilter?.value.licenseFilter
        ? { licenseFilter: agentsDrawerFilter?.GQL_AgentsFilter?.value.licenseFilter.filter(license => license !== Agent_LicenseFilter.CloudHelper) }
        : {
            licenseFilter: [
              Agent_LicenseFilter.NoLicense,
              Agent_LicenseFilter.OnlyCiInitiator,
              Agent_LicenseFilter.OnlyInitiator,
              Agent_LicenseFilter.HelperAndCiInitiator,
              Agent_LicenseFilter.HelperAndInitiator,
              Agent_LicenseFilter.OnPremHelper,
            ],
          };
    }
    return agentsDrawerFilter?.GQL_AgentsFilter?.value || {};
  };

  const selectByFilter = async (filterType: AgentsFilterSelection) => {
    try {
      if (!loading.value) {
        clearSelection();
        const payload = {
          filter: {
            coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
            ...(getFilterForPayload(filterType) as Agent_WhereInput),
            ...(agentsQueryFilter?.get.value || {}),
            ...FILTER_SELECTION_MAP[filterType],
            paging: undefined,
          },
        };
        loading.value = true;
        const agents = await apolloClient.query<AgentNamesQuery, AgentNamesQueryVariables>({
          query: AgentNamesDocument,
          fetchPolicy: 'no-cache',
          variables: payload,
        });
        selectedAgentIds.value = new Set(
          agents.data.agents?.agents?.filter(agent => agent && agent?.status !== Agent_Status.Stopped && agent?.status !== Agent_Status.Updating)?.map(agent => agent?.id as string),
        );
      }
    } finally {
      loading.value = false;
    }
  };

  const selectByIds = (newSelectedAgentIds: typeof selectedAgentIds.value) => {
    selectedAgentIds.value = newSelectedAgentIds;
  };
  const providePayload = { selectedAgentIds, loading, selectByIds, selectByFilter, clearSelection, removeAgentFromSelectedAgentIds };

  app.provide(IKAgentsSelected, providePayload);
};

export const useAgentsTableHelpers = () => {
  const getFreeDiskSpace = (agent: Agent) => ({
    total: ((Number(agent.performance?.totalDisk) || 0) / 1024 / 1024 / 1024).toFixed(1),
    available: ((Number(agent.performance?.availDisk) || 0) / 1024 / 1024 / 1024).toFixed(1),
  });
  const getMemoryUsage = (agent: Agent) => ({
    total: ((Number(agent.performance?.totalMemory) || 0) / 1024 / 1024 / 1024).toFixed(1),
    available: ((Number(agent.performance?.availMemory) || 0) / 1024 / 1024 / 1024).toFixed(1),
  });
  return { getFreeDiskSpace, getMemoryUsage };
};

export const useAgentRowStyle = () => {
  const style = {
    unselected: {
      default: { background: COLORS['System/app_white'], color: COLORS['Text/text_high'] } as StyleValue,
      offline: { background: COLORS['System/app_white'], color: COLORS['Text/text_lowest'], borderColor: COLORS['Hovers/active_grey'] } as StyleValue,
      duplicateLicense: { background: COLORS['System/app_white'], color: COLORS['Text/text_medium'] } as StyleValue,
      incompatibleVersion: { color: COLORS['Text/text_medium'] } as StyleValue,
      inProgress: { color: COLORS['Text/text_medium'] } as StyleValue,
      incompatibleVersionAndOffline: { color: COLORS['Text/text_medium'], borderColor: COLORS['Hovers/active_grey'] } as StyleValue,
    },
    selected: {
      default: { background: '#E6F8F9', color: COLORS['Text/text_high'] } as StyleValue,
      incompatibleVersion: { background: '#E6F8F9', color: COLORS['Text/text_medium'] } as StyleValue,
    },
  } as const;

  /**
   * "Returning the style of the agent row based on the status of the agent."
   *
   * @param {Agent} agent - The agent object.
   * @param {boolean} [selected] - A boolean value that indicates whether the agent is selected or not.
   * @returns The style of the agent row based on the status of the agent.
   */
  const getAgentRowStyle = (agent: Agent, selected?: boolean) => {
    const { isDuplicateLicense, isIncompatibleVersion, isInProgress, isOffline } = agent?.metadata || {};
    /* If the agent is selected, return the incompatible version style if the agent is incompatible, otherwise return the default selected style. */
    if (selected) return isIncompatibleVersion ? style.selected.incompatibleVersion : style.selected.default;

    /* Returning the style of the agent row based on the status of the agent. */
    if (isOffline && isIncompatibleVersion) return style.unselected.incompatibleVersionAndOffline;
    if (isOffline || agent?.status === Agent_Status.Stopped) return style.unselected.offline;
    if (isDuplicateLicense) return style.unselected.duplicateLicense;
    if (isInProgress) return style.unselected.inProgress;
    if (isIncompatibleVersion) return style.unselected.incompatibleVersion;
    return style.unselected.default;
  };
  return { getAgentRowStyle };
};
